import { formatCount, pluralRuIndex } from './format';

/**
 * How firmly a buyer's supplier concentration may be STATED, given how many
 * contracts it is computed from. One rule for both surfaces that render it —
 * the buyer profile's «Концентрация и фаворитизм» block and the tender-detail
 * concentration panel — so the same buyer never reads one way on one page and
 * another way on the other.
 *
 * Owner's decision (Igor, 2026-10-07): clients asked «что такое HHI», so the
 * index never appears on screen — only in a hover `title` — and a verdict off a
 * handful of contracts must not shout: «Не надо скрывать, просто не надо
 * кричать, что это критически высокая концентрация». The FIGURES (contracts,
 * suppliers, shares, the ranked list) always render from 1 contract; what
 * scales with the count is the verdict's confidence:
 *
 *   - 1–4 contracts → `early`: no verdict, one neutral «N контрактов — о
 *     концентрации судить рано» line — the only grey state;
 *   - {@link CONCENTRATION_HEDGE_MIN_CONTRACTS}–9 → `hedged`: the same plain-words
 *     verdict followed by «· N контрактов», in the band's colour (Igor, 2026-10-10 — it
 *     was the neutral grey pill until then), no second line;
 *   - {@link CONCENTRATION_VERDICT_MIN_CONTRACTS}+ → `verdict`: plain words in the
 *     band's colour plus the «зайти сложно/можно/легко» line.
 *
 * The colour comes from {@link concentrationPillLevel}, one band for both.
 * The contract count is TENDERS only — direct contracts bought without a
 * competition are excluded on the server (`competitive-awards.ts`).
 *
 * Bands are the standard HHI thresholds (US DOJ/FTC): ≥2500 high, 1500–2499
 * moderate, <1500 low. The «зависимость от одного поставщика» flag is the loud
 * form of the same claim, so it is shown only at `verdict` strength — see
 * {@link mayAssertConcentration}.
 */
export const CONCENTRATION_HEDGE_MIN_CONTRACTS = 5;
export const CONCENTRATION_VERDICT_MIN_CONTRACTS = 10;

export type ConcentrationLevel = 'high' | 'moderate' | 'low';

export type ConcentrationRead =
  | { kind: 'none' }
  | { kind: 'early'; contracts: number }
  | { kind: 'hedged'; level: ConcentrationLevel; contracts: number }
  | { kind: 'verdict'; level: ConcentrationLevel };

export function concentrationLevel(hhi: number): ConcentrationLevel {
  return hhi >= 2500 ? 'high' : hhi >= 1500 ? 'moderate' : 'low';
}

/** @param contracts the contracts the concentration is computed FROM (awards
 *  with an identified winner), not the buyer's headline award count. */
export function concentrationRead(hhi: number, contracts: number): ConcentrationRead {
  if (!(contracts >= 1)) return { kind: 'none' };
  if (contracts < CONCENTRATION_HEDGE_MIN_CONTRACTS) return { kind: 'early', contracts };
  if (contracts < CONCENTRATION_VERDICT_MIN_CONTRACTS) {
    return { kind: 'hedged', level: concentrationLevel(hhi), contracts };
  }
  return { kind: 'verdict', level: concentrationLevel(hhi) };
}

/** Whether the LOUD form of the claim — the single-supplier dependence flag and
 *  the «зайти сложно/можно/легко» line — may be made off this many contracts.
 *  (The pill's colour is no longer part of it: 5–9 is coloured too.) */
export function mayAssertConcentration(contracts: number): boolean {
  return contracts >= CONCENTRATION_VERDICT_MIN_CONTRACTS;
}

/** The pill's colour band: the HHI level for `hedged` (5–9) and `verdict`
 *  (10+) alike — same thresholds, same colours (Igor, 2026-10-10). Null for
 *  `none`/`early`, which render no pill. */
export function concentrationPillLevel(read: ConcentrationRead): ConcentrationLevel | null {
  return read.kind === 'hedged' || read.kind === 'verdict' ? read.level : null;
}

type T = (k: string, params?: Record<string, string | number>) => string;

/** The index itself, for the hover `title` only — «Индекс концентрации HHI 3 200».
 *  Space-grouped by hand (`formatCount`): Chrome has no `uz` CLDR data. */
export function hhiTitle(t: T, hhi: number): string | undefined {
  return Number.isFinite(hhi) ? t('supplierGraph.hhiTitle', { n: formatCount(hhi) }) : undefined;
}

const PLURAL_FORMS = ['one', 'few', 'many'] as const;

/** «7 контрактов» / «3 контракта» / «1 контракт» — the noun agrees in Russian. */
function contractsLabel(t: T, n: number): string {
  return t(`supplierGraph.contracts.${PLURAL_FORMS[pluralRuIndex(n)]}`, { n });
}

/** The sentence a non-loud read renders: «3 контракта — о концентрации судить
 *  рано» (`early`) or «Закупки держат 1–2 поставщика · 7
 *  контрактов» (`hedged`). Null for `none` and `verdict`. */
export function concentrationSentence(t: T, read: ConcentrationRead): string | null {
  if (read.kind === 'early') {
    return t('supplierGraph.tooEarly', { contracts: contractsLabel(t, read.contracts) });
  }
  if (read.kind === 'hedged') {
    return t(`supplierGraph.concentrationHedged.${read.level}`, {
      contracts: contractsLabel(t, read.contracts),
    });
  }
  return null;
}
